/**
 * 路徑語法與萬用字元比對。對應 docs/protocol.md §2、§7.1、§7.2。
 * 純函式，無狀態。
 */

import { MAX_PATH_DEPTH, MAX_PATH_LENGTH, MAX_SEGMENT_LENGTH } from './constants.ts'
import { busError } from './errors.ts'
import type { BusError } from './types.ts'

const NORMAL_SEGMENT = /^[a-z0-9][a-z0-9_-]*$/
const RESERVED_SEGMENT = /^\$[a-z0-9][a-z0-9_-]*$/

export function isReserved(segment: string): boolean {
  return segment.startsWith('$')
}

/** 路徑是否包含任一保留段（§5：`$` = 核心擁有） */
export function hasReservedSegment(path: string): boolean {
  return splitPath(path).some(isReserved)
}

export function splitPath(path: string): string[] {
  return path.split('/')
}

export function joinPath(...parts: string[]): string {
  return parts.filter((p) => p.length > 0).join('/')
}

function checkSegment(segment: string): string | null {
  if (segment.length === 0) return '空的路徑段（不得有連續、開頭或結尾的 "/"）'
  if (segment.length > MAX_SEGMENT_LENGTH) {
    return `路徑段 "${segment}" 超過 ${MAX_SEGMENT_LENGTH} 字元`
  }
  if (isReserved(segment)) {
    if (!RESERVED_SEGMENT.test(segment)) {
      return `保留段 "${segment}" 格式錯誤，須為 $ 後接 [a-z0-9][a-z0-9_-]*`
    }
    return null
  }
  if (!NORMAL_SEGMENT.test(segment)) {
    return `路徑段 "${segment}" 不合法，只允許小寫 [a-z0-9][a-z0-9_-]*`
  }
  return null
}

/** 驗證一個具體路徑（不含萬用字元）。合法回傳 null。 */
export function validatePath(path: string): BusError | null {
  if (typeof path !== 'string' || path.length === 0) {
    return busError('E_INVALID_PATH', '路徑必須是非空字串', { path: String(path) })
  }
  if (path.length > MAX_PATH_LENGTH) {
    return busError('E_INVALID_PATH', `路徑超過 ${MAX_PATH_LENGTH} 字元`, { path })
  }
  const segments = splitPath(path)
  if (segments.length > MAX_PATH_DEPTH) {
    return busError('E_INVALID_PATH', `路徑深度超過 ${MAX_PATH_DEPTH} 段`, { path })
  }
  for (const segment of segments) {
    if (segment === '+' || segment === '#') {
      return busError('E_INVALID_PATH', '具體路徑不得包含萬用字元', { path })
    }
    const problem = checkSegment(segment)
    if (problem) return busError('E_INVALID_PATH', problem, { path })
  }
  return null
}

/** 驗證一個訂閱模式（可含萬用字元）。合法回傳 null。 */
export function validatePattern(pattern: string): BusError | null {
  if (typeof pattern !== 'string' || pattern.length === 0) {
    return busError('E_INVALID_PATH', '模式必須是非空字串', { path: String(pattern) })
  }
  if (pattern.length > MAX_PATH_LENGTH) {
    return busError('E_INVALID_PATH', `模式超過 ${MAX_PATH_LENGTH} 字元`, { path: pattern })
  }
  const segments = splitPath(pattern)
  if (segments.length > MAX_PATH_DEPTH) {
    return busError('E_INVALID_PATH', `模式深度超過 ${MAX_PATH_DEPTH} 段`, { path: pattern })
  }
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i] as string
    if (segment === '+') continue
    if (segment === '#') {
      if (i !== segments.length - 1) {
        return busError('E_INVALID_PATH', '"#" 只能出現在模式結尾', { path: pattern })
      }
      continue
    }
    const problem = checkSegment(segment)
    if (problem) return busError('E_INVALID_PATH', problem, { path: pattern })
  }
  return null
}

/**
 * 萬用字元比對。§7.2：
 * 位於**模式第一段**的 `+` 或 `#` 不匹配以 `$` 開頭的段；
 * 非第一段的萬用字元則會匹配（agent 需要 `app/{uid}/#` 一次拿到含 $window 的全部）。
 */
export function matchPattern(pattern: string, path: string): boolean {
  return matchSegments(splitPath(pattern), splitPath(path))
}

export function matchSegments(patternSegs: readonly string[], pathSegs: readonly string[]): boolean {
  let p = 0
  let t = 0

  while (p < patternSegs.length) {
    const pat = patternSegs[p] as string

    if (pat === '#') {
      // 匹配零段或多段
      if (p === 0 && pathSegs.length > 0 && isReserved(pathSegs[0] as string)) return false
      return true
    }

    if (t >= pathSegs.length) return false
    const seg = pathSegs[t] as string

    if (pat === '+') {
      if (p === 0 && isReserved(seg)) return false
      p++
      t++
      continue
    }

    if (pat !== seg) return false
    p++
    t++
  }

  return t === pathSegs.length
}

/** 該模式是否可能匹配到這個前綴底下的東西（用於粗篩，非精確） */
export function isUnderPrefix(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(prefix.endsWith('/') ? prefix : `${prefix}/`)
}
